// Project import configuration using the base configuration
import { createBaseImportConfig, FieldTypes, CommonFields } from './baseImportConfig';
import { importApi } from '../../../services/api';
import ClientSelector from '../../common/ClientSelector';
import ClientContractTypeSelector from '../../common/ClientContractTypeSelector';
import EmployeeSearch from '../../common/EmployeeSearch';
import BusinessAreaSelector from '../../common/BusinessAreaSelector';
import BusinessUnitSelector from '../../common/BusinessUnitSelector';
import { CLIENT_RETURN_TYPES, CONTRACT_TYPE_RETURN_TYPES, EMPLOYEE_RETURN_TYPES, BUSINESS_AREA_RETURN_TYPES, BUSINESS_UNIT_RETURN_TYPES } from '../../../constants/selectorReturnTypes';

const projectConfig = {
  entityName: 'Project',
  entityNamePlural: 'Projects',
  
  requiredFields: [
    'name', 
    'code', 
    'startDate', 
    'clientName',
    'businessUnitName'
  ],
  optionalFields: ['description', 'contractTypeName', 'businessAreaName', 'managerEmail'],
  
  // Field configurations using common field helpers
  fields: {
    name: CommonFields.name('Project Name'),
    code: CommonFields.code('Project Code'),
    description: CommonFields.description(),
    startDate: {
      type: FieldTypes.TEXT, 
      label: 'Start Date',
      required: true
    },
    clientName: {
      ...CommonFields.clientSelector('Client', true),
      component: ClientSelector,
      returnType: CLIENT_RETURN_TYPES.NAME
    },
    businessUnitName: {
      type: FieldTypes.SELECTOR,
      label: 'Business Unit',
      component: BusinessUnitSelector,
      returnType: BUSINESS_UNIT_RETURN_TYPES.NAME,
      required: true
    },
    contractTypeName: {
      type: FieldTypes.SELECTOR,
      label: 'Contract Type',
      component: ClientContractTypeSelector,
      returnType: CONTRACT_TYPE_RETURN_TYPES.NAME,
      required: false
    },
    businessAreaName: {
      type: FieldTypes.SELECTOR,
      label: 'Business Area',
      component: BusinessAreaSelector,
      returnType: BUSINESS_AREA_RETURN_TYPES.NAME,
      required: false
    },
    managerEmail: {
      type: FieldTypes.SELECTOR,
      label: 'Manager',
      component: EmployeeSearch,
      returnType: EMPLOYEE_RETURN_TYPES.EMAIL,
      required: false,
      props: {
        hideLabel: true,
        placeholder: 'Search for project manager...',
        showSelectedAvatar: true,
        width: 300,
        returnValue: EMPLOYEE_RETURN_TYPES.EMAIL
      }
    }
  },
  
  // Table column order
  tableColumns: [
    { field: 'status', label: 'Status', width: 120 },
    { field: 'name', label: 'Project Name', width: 200 },
    { field: 'code', label: 'Project Code', width: 150 },
    { field: 'clientName', label: 'Client', width: 180 },
    { field: 'businessUnitName', label: 'Business Unit', width: 180 },
    { field: 'startDate', label: 'Start Date', width: 120 },
    { field: 'contractTypeName', label: 'Contract Type', width: 150 },
    { field: 'businessAreaName', label: 'Business Area', width: 180 },
    { field: 'managerEmail', label: 'Representative', width: 200 },
    { field: 'actions', label: 'Actions', width: 80 }
  ],
  
  // API endpoints
  api: {
    validate: importApi.validateProjects,
    import: importApi.importProjects
  },
  
  // Status configurations
  statusConfig: {
    projectStatuses: {
      'checking': { 
        label: 'Checking...', 
        color: '#6366F1',
        tooltip: 'Validation in progress'
      },
      'ok_to_import': { 
        label: 'Ready to Import', 
        color: '#10B981',
        tooltip: 'Project doesn\'t exist and is ready for import'
      },
      'already_project': { 
        label: 'Already Exists', 
        color: '#F59E0B',
        tooltip: 'Project already exists in the system'
      }
    },
    validationStatuses: {
      'valid': { label: 'Valid', color: '#10B981' },
      'invalid': { label: 'Invalid', color: '#EF4444' },
      'pending': { label: 'Pending', color: '#6366F1' }
    },
    importStatuses: {
      'success': { label: 'Imported', color: '#059669', backgroundColor: '#ECFDF5' },
      'failed': { label: 'Import Failed', color: '#DC2626', backgroundColor: '#FEF2F2' }
    }
  },
  
  // Documentation only - status configurations use defaults from base config
  documentation: {
    description: 'Upload a CSV file with project data to import into the system',
    fileFormat: {
      dateFormat: 'YYYY-MM-DD',
      notes: [
        'Project code must be unique across all projects',
        'clientName should be selected from the dropdown of existing clients in the system',
        'businessUnitName should be selected from the dropdown of existing business units in the system',
        'businessAreaName should be selected from the dropdown of existing business areas in the system',
        'manager should be selected from the dropdown of existing employees in the system'
      ]
    }
  },
  
  // Import logic helpers
  helpers: {
    getImportableItems: (projects) => {
      return projects.filter(project => 
        project.projectStatus === 'ok_to_import' && 
        project.validationStatus === 'valid'
      );
    },
    
    // Backwards compatibility
    getImportableProjects: function(projects) {
      return this.getImportableItems(projects);
    },
    
    getStatusCounts: (projects) => {
      return {
        valid: projects.filter(project => project.validationStatus === 'valid').length,
        invalid: projects.filter(project => project.validationStatus === 'invalid').length,
        importable: projects.filter(project => 
          project.projectStatus === 'ok_to_import' && 
          project.validationStatus === 'valid'
        ).length,
        alreadyProject: projects.filter(project => project.projectStatus === 'already_project').length
      };
    },
    
    // Backwards compatibility
    getProjectStatusCounts: function(projects) {
      return this.getStatusCounts(projects);
    },
    
    // Custom processing for project validation response
    processValidationResponse: (response) => {
      return response.rows.map((row, index) => ({
        ...row.data,
        id: `temp_${Date.now()}_${index}`,
        validationStatus: row.validationStatus,
        validationErrors: row.validationErrors || [],
        projectStatus: row.projectStatus,
        editable: row.editable,
        rowIndex: row.rowIndex
      }));
    },
    
    // Custom import tooltip messages for projects
    getImportTooltip: (stats, config) => {
      // Check for project-specific status messages
      if (stats.alreadyProject > 0) {
        return `All ${config.entityNamePlural.toLowerCase()} already exist in the system`;
      }
      // Fall back to default
      return `No ${config.entityNamePlural.toLowerCase()} ready to import`;
    }
  }
};

export const projectImportConfig = createBaseImportConfig(projectConfig); 